import { useAtomValue, useSetAtom } from 'jotai';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DateInput, TimeInput } from '@/lib/v2/components';
import { eventOnChangeDateInput } from '@/lib/v2/components/DateInput/DateInput';
import { eventOnChangeTimeInput } from '@/lib/v2/components/TimeInput/TimeInput';

import { useUserData } from '@/src/application/hooks/useUserData';
import DateConverter from '@/src/application/util/DateConverter';
import useSchedulingForm, {
  SchedulingSmsForm,
} from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/components/SmsScheduling/hooks/useSchedulingForm';

import { atomProcessingAction } from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import { atomCallbackStep } from '@/modules/CampaignsModule/atoms/actionsAtom';
import { atomActionInfoSms } from '@/modules/CampaignsModule/atoms/actionSMSAtom';
import { CAMPAIGNS_PATH, NEXT_YEAR_OFFSET } from '@/modules/CampaignsModule/constants';
import { useActionSmsData } from '@/modules/CampaignsModule/hooks/useActionSmsData';
import { IScheduledSMSDeliveryV1 } from '@/modules/CampaignsModule/interfaces/CampaignActions';

const TRANSLATE_SCHEDULING = 'CAMPAIGN_ACTIONS_MAIN.SMS_ACTION.SCHEDULING';
interface IDeliveryFormProps {
  disabled?: boolean;
}

const SchedulingDeliveryForm = ({ disabled = false }: IDeliveryFormProps) => {
  /* cspell:disable */
  const { t } = useTranslation();
  const { campaignId, actionId } = useParams();
  const navigate = useNavigate();

  const setProcessingAction = useSetAtom(atomProcessingAction);
  const setCallbackStep = useSetAtom(atomCallbackStep);
  const actionInfoSms = useAtomValue(atomActionInfoSms);

  const [userData] = useUserData();
  const { control, RULES, handleSubmit, setValue } = useSchedulingForm();
  const { setScheduledSMSDelivery } = useActionSmsData(Number(actionId));

  const formatGMT = useMemo(
    () => (userData.timeOffset ? `GMT${userData.timeOffset}` : undefined),
    [userData.timeOffset]
  );

  const currentDateRoot = useMemo(
    () => (formatGMT ? DateConverter.toGMTDate(new Date().toISOString(), formatGMT) : undefined),
    [formatGMT]
  );

  const dateSms = useMemo(() => {
    if (!actionInfoSms) return;

    const { anio, mes, dia, hora, minutos } = actionInfoSms;

    const dateTime = new Date(
      parseInt(anio),
      parseInt(mes) - 1,
      parseInt(dia),
      parseInt(hora),
      parseInt(minutos)
    );

    const date = new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));

    return {
      date,
      dateTime,
    };
  }, [actionInfoSms]);

  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    !actionInfoSms?.inmediato ? dateSms?.date : undefined
  );

  const [scheduledTime, setScheduledTime] = useState<Date | undefined>(
    !actionInfoSms?.inmediato ? dateSms?.dateTime : undefined
  );
  const [scheduledTimeMinDateTime, setScheduledTimeMinDateTime] = useState<Date | undefined>(
    undefined
  );

  const maxDateScheduledDelivery = useMemo(() => {
    if (!formatGMT) return undefined;
    const date = DateConverter.toGMTDate(new Date().toISOString(), formatGMT);
    date.setFullYear(date.getFullYear() + NEXT_YEAR_OFFSET);
    return date;
  }, [formatGMT]);

  useEffect(() => {
    if (!dateSms?.date || !formatGMT) return;

    const dateSmsGMT = new Date(dateSms.dateTime);
    setValue('sendDate', dateSmsGMT);
    setValue('sendTime', dateSmsGMT);

    const currentDateGMT = DateConverter.toGMTDate(new Date().toISOString(), formatGMT);

    if (
      dateSmsGMT.getDate() === currentDateGMT.getDate() &&
      dateSmsGMT.getMonth() === currentDateGMT.getMonth() &&
      dateSmsGMT.getFullYear() === currentDateGMT.getFullYear()
    ) {
      setScheduledTimeMinDateTime(dateSmsGMT > currentDateGMT ? currentDateGMT : dateSmsGMT);
    } else {
      setScheduledTimeMinDateTime(undefined);
    }
  }, [setValue, formatGMT]);

  const handleSubmitData = useCallback(
    async (formData: SchedulingSmsForm) => {
      if (!actionId || !userData.timeOffset) return;

      setProcessingAction(true);

      const { sendDate, sendTime } = formData;
      if (!sendDate || !sendTime) return;

      const utcDate = new Date(
        Date.UTC(sendDate.getFullYear(), sendDate.getMonth(), sendDate.getDate(), 0, 0, 0)
      );

      const gmtOffset = parseInt(userData.timeOffset.toString());
      const schedulingTimestamp = utcDate.getTime() - gmtOffset * 60 * 60 * 1000;

      const queryParams: IScheduledSMSDeliveryV1 = {
        elementoId: +actionId,
        hora: sendTime.getHours(),
        minuto: sendTime.getMinutes(),
        milis: schedulingTimestamp,
      };

      await setScheduledSMSDelivery(queryParams);

      setProcessingAction(false);
      navigate(`${CAMPAIGNS_PATH}/${Number(campaignId)}/sms/${Number(actionId)}/summary`);
    },
    [
      actionId,
      setProcessingAction,
      setScheduledSMSDelivery,
      userData.timeOffset,
      navigate,
      campaignId,
    ]
  );

  const onChangeReinforcementDate: eventOnChangeDateInput = useCallback(
    (event) => {
      if (!formatGMT) return;
      const currentDate = DateConverter.toGMTDate(new Date().toISOString(), formatGMT);
      const inputDate = event as Date;

      setScheduledDate(inputDate);
      setScheduledTime(undefined);

      if (
        inputDate.getDate() === currentDate.getDate() &&
        inputDate.getMonth() === currentDate.getMonth() &&
        inputDate.getFullYear() === currentDate.getFullYear()
      ) {
        setScheduledTimeMinDateTime(currentDate);
      } else {
        setScheduledTimeMinDateTime(undefined);
      }
    },
    [formatGMT]
  );

  const onChangeReinforcementTime: eventOnChangeTimeInput = useCallback((event) => {
    setScheduledTime(event as Date);
  }, []);

  useEffect(() => {
    setCallbackStep(() => handleSubmit(handleSubmitData));
    return () => setProcessingAction(false);
  }, [handleSubmit, handleSubmitData, setCallbackStep, setProcessingAction]);

  /* cspell:enable */
  return (
    <form
      className="pt-12"
      id="immediate-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        handleSubmit(handleSubmitData);
      }}
    >
      <div className="flex gap-3">
        <div className="flex w-200">
          <DateInput
            fullWidth
            isRequired
            control={control}
            date={scheduledDate}
            disabled={disabled}
            id="sendDate"
            label={t(`${TRANSLATE_SCHEDULING}.date`)}
            maxDate={maxDateScheduledDelivery}
            minDate={currentDateRoot}
            name="sendDate"
            placeholder={t(`${TRANSLATE_SCHEDULING}.datePlaceholder`)}
            rules={RULES.sendDate}
            onChange={onChangeReinforcementDate}
          />
        </div>
        <div className="flex w-[180px]">
          <TimeInput
            isRequired
            control={control}
            date={scheduledTime}
            disabled={disabled}
            id="sendTime"
            label={t(`${TRANSLATE_SCHEDULING}.time`)}
            minTime={scheduledTimeMinDateTime}
            name="sendTime"
            placeholder={t(`${TRANSLATE_SCHEDULING}.timePlaceholder`)}
            rules={RULES.sendTime}
            onChange={onChangeReinforcementTime}
          />
        </div>
      </div>
    </form>
  );
};

export default SchedulingDeliveryForm;
